/**
 * Bero platform console — Audit log. GET /api/platform/audit lists every
 * PlatformAudit row (SUPER_ADMIN only, reverse-chron, capped at 200) — every
 * write elsewhere in the console (enter society, create society, plan
 * change, lead update, invite sent/accepted, team change) appends one of
 * these. Filter chips narrow to a single action server-side.
 */
import React from 'react';
import { ScrollView, View } from 'react-native';

import { space } from '@bero/tokens';
import { Icon } from '@/features/account-admin/icons';
import { timeAgo, useFetch } from '@/features/community/lib';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Chip, EmptyState, Hairline, Screen, Skeleton } from '@/ui';

type AuditRow = {
  id: number; actorUserId: number; action: string; societyId: number | null;
  detail: string; createdAt: string | null; actorName: string; societyName: string | null;
};

const ACTIONS: readonly string[] = [
  'ENTER_SOCIETY', 'CREATE_SOCIETY', 'PLAN_CHANGE', 'LEAD_UPDATE',
  'INVITE_SENT', 'INVITE_ACCEPTED', 'MEMBER_CHANGE',
];
const ACTION_LABEL: Record<string, DictKey> = {
  ENTER_SOCIETY: 'plat_audit_action_enter_society',
  CREATE_SOCIETY: 'plat_audit_action_create_society',
  PLAN_CHANGE: 'plat_audit_action_plan_change',
  LEAD_UPDATE: 'plat_audit_action_lead_update',
  INVITE_SENT: 'plat_audit_action_invite_sent',
  INVITE_ACCEPTED: 'plat_audit_action_invite_accepted',
  MEMBER_CHANGE: 'plat_audit_action_member_change',
};

async function fetchAudit(action: string | null): Promise<AuditRow[]> {
  const qs = action ? `?action=${encodeURIComponent(action)}` : '';
  const body = await api.get<{ items: AuditRow[] }>(`/api/platform/audit${qs}`);
  return Array.isArray(body.items) ? body.items : [];
}

function AuditRowItem({ row }: { row: AuditRow }) {
  const { t } = useT();
  return (
    <View style={{ paddingVertical: space.s3, paddingHorizontal: space.s2, gap: 2 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, flexWrap: 'wrap' }}>
        <AppText variant="bodyMd" weight="500">{row.actorName}</AppText>
        <AppText variant="bodySm" tone="tertiary">{ACTION_LABEL[row.action] ? t(ACTION_LABEL[row.action]) : row.action}</AppText>
        {row.societyName ? <AppText variant="bodySm" tone="tertiary">· {row.societyName}</AppText> : null}
        <View style={{ flex: 1 }} />
        <AppText variant="bodySm" tone="tertiary">
          {row.createdAt ? timeAgo(new Date(row.createdAt)) : '—'}
        </AppText>
      </View>
      {row.detail ? <AppText variant="bodySm" tone="secondary">{row.detail}</AppText> : null}
    </View>
  );
}

export default function PlatformAuditScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [filter, setFilter] = React.useState<string | null>(null);
  const { data, loading, error, refetch } = useFetch(() => fetchAudit(filter), [filter]);
  const rows = data ?? [];

  return (
    <Screen padded fullWidth style={{ paddingTop: space.s5 }}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ flexDirection: 'row', gap: space.s2, paddingBottom: space.s4 }}
      >
        <Chip label={t('plat_audit_action_all')} active={filter === null} onPress={() => setFilter(null)} />
        {ACTIONS.map((a) => (
          <Chip key={a} label={t(ACTION_LABEL[a])} active={filter === a} onPress={() => setFilter(a)} />
        ))}
      </ScrollView>
      {loading ? (
        <View style={{ gap: space.s2 }}>
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} h={48} />)}
        </View>
      ) : error ? (
        <EmptyState
          icon={<Icon name="alert-triangle" size={40} color={colors.textTertiary} />}
          title={t('plat_audit_err')}
          cta={t('retry')}
          onCta={() => void refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Icon name="clock" size={40} color={colors.textTertiary} />}
          title={t('plat_audit_empty')}
        />
      ) : (
        <View>
          {rows.map((row, i) => (
            <View key={row.id}>
              <AuditRowItem row={row} />
              {i < rows.length - 1 ? <Hairline /> : null}
            </View>
          ))}
        </View>
      )}
    </Screen>
  );
}
